import React, { useState, useEffect } from 'react';
import { UltrasonicTestReport, UltrasonicTestItem } from '../types/ndt';
import { pdf } from '@react-pdf/renderer';
import UTReportPDF from './UTReportPDF';

interface EditableUTReportProps {
  report: UltrasonicTestReport;
  onSave: (report: UltrasonicTestReport) => Promise<void>;
  onClose: () => void;
  isSaving?: boolean;
}

const EditableUTReport: React.FC<EditableUTReportProps> = ({
  report,
  onSave,
  onClose,
  isSaving = false
}) => {
  const [editableReport, setEditableReport] = useState<UltrasonicTestReport>(report);
  const [isEditing, setIsEditing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    setEditableReport(report);
  }, [report]);

  const handleInputChange = (field: string, value: any) => {
    setEditableReport(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  const handleNestedInputChange = (parentField: string, childField: string, value: any) => {
    setEditableReport(prev => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField as keyof UltrasonicTestReport] as Record<string, any>),
        [childField]: value
      },
      updatedAt: new Date().toISOString()
    }));
  };

  const handleItemChange = (index: number, field: keyof UltrasonicTestItem, value: any) => {
    const updatedItems = [...editableReport.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setEditableReport(prev => ({
      ...prev,
      items: updatedItems,
      updatedAt: new Date().toISOString()
    }));
  };

  const addItem = () => {
    const newItem: UltrasonicTestItem = {
      srNo: editableReport.items.length + 1,
      jobDescription: '',
      heatNumber: '',
      quantity: 1,
      observation: '',
      remarks: ''
    };
    setEditableReport(prev => ({
      ...prev,
      items: [...prev.items, newItem],
      updatedAt: new Date().toISOString()
    }));
  };

  const removeItem = (index: number) => {
    if (editableReport.items.length > 1) {
      const updatedItems = editableReport.items.filter((_, i) => i !== index);
      const renumberedItems = updatedItems.map((item, i) => ({
        ...item,
        srNo: i + 1
      }));
      setEditableReport(prev => ({
        ...prev,
        items: renumberedItems,
        updatedAt: new Date().toISOString()
      }));
    }
  };

  const handleSave = async () => {
    try {
      await onSave(editableReport);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving report:', error);
    }
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const doc = <UTReportPDF report={editableReport} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `UT_Report_${editableReport.reportNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 font-sans text-sm md:p-8">
      <div className="mx-auto max-w-7xl rounded-lg bg-white p-6 shadow-xl ring-1 ring-gray-900/5 md:p-12">
        {/* Header Section - Centered & Clean */}
        <header className="mb-6 flex flex-col items-center border-b-2 border-gray-300 pb-4">
          {/* Top Line: || Shri Swami Samarth || */}
          <div className="text-center font-bold text-gray-700 mb-2">
            || Shri Swami Samarth ||
          </div>

          {/* Logo + Company Name + Tagline in one row */}
          <div className="flex items-center gap-4 mb-2">
            <img src="/AVIS FINAL LOGO png.png" alt="AVIS Logo" className="h-16 w-auto" />
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-800">AVI'S ENGINEERING & CONSULTANCY</h1>
              <p className="text-sm font-semibold text-gray-600">NDT SERVICES</p>
            </div>
          </div>

          {/* Contact Info Block - Centered */}
          <div className="mt-2 text-center text-xs leading-tight">
            <p>
              SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Savedi, Ahilyanagar (MS)
            </p>
            <p>
              Ph: 7887985350 | Email: avis.engineering2025@gmail.com
            </p>
            <p>
              GSTIN: 27FLTPM3802F1ZU | PAN: AAMPM4068R
            </p>
          </div>
        </header>

        {/* Action Buttons */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Edit Report
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditableReport(report);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {isGeneratingPDF ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Title Section */}
        <div className="mb-6 rounded border-2 border-black p-2 text-center text-lg font-bold">
          ULTRASONIC TEST REPORT
        </div>

        {/* Details Section */}
        <div className="mb-6 grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="flex">
            <span className="font-bold">To,</span>
          </div>
          <div className="flex">
            <span className="font-bold">Report No. :</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.reportNumber}
                onChange={(e) => handleInputChange('reportNumber', e.target.value)}
                className="flex-1 border-b border-dotted border-gray-400 pl-2 bg-transparent focus:outline-none focus:border-blue-500"
              />
            ) : (
              <span className="flex-1 border-b border-dotted border-gray-400 pl-2">{editableReport.reportNumber}</span>
            )}
          </div>
          <div className="flex">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.client.name}
                onChange={(e) => handleNestedInputChange('client', 'name', e.target.value)}
                className="font-bold bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
              />
            ) : (
              <span className="font-bold">{editableReport.client.name}</span>
            )}
          </div>
          <div className="flex">
            <span className="font-bold">Order No. :</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.orderNumber}
                onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                className="flex-1 border-b border-dotted border-gray-400 pl-2 bg-transparent focus:outline-none focus:border-blue-500"
              />
            ) : (
              <span className="flex-1 border-b border-dotted border-gray-400 pl-2">{editableReport.orderNumber}</span>
            )}
          </div>
          <div className="flex">
            {isEditing ? (
              <textarea
                value={editableReport.client.address}
                onChange={(e) => handleNestedInputChange('client', 'address', e.target.value)}
                className="font-bold bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full resize-none"
                rows={2}
              />
            ) : (
              <span className="font-bold">{editableReport.client.address}</span>
            )}
          </div>
          <div className="flex">
            <span className="font-bold">Order Date :</span>
            {isEditing ? (
              <input
                type="date"
                value={editableReport.orderDate}
                onChange={(e) => handleInputChange('orderDate', e.target.value)}
                className="flex-1 border-b border-dotted border-gray-400 pl-2 bg-transparent focus:outline-none focus:border-blue-500"
              />
            ) : (
              <span className="flex-1 border-b border-dotted border-gray-400 pl-2">{editableReport.orderDate}</span>
            )}
          </div>
          <div className="col-span-2 flex items-center">
            <span className="font-bold">Date of Test :</span>
            {isEditing ? (
              <input
                type="date"
                value={editableReport.testDate}
                onChange={(e) => handleInputChange('testDate', e.target.value)}
                className="flex-1 border-b border-dotted border-gray-400 pl-2 bg-transparent focus:outline-none focus:border-blue-500"
              />
            ) : (
              <span className="flex-1 border-b border-dotted border-gray-400 pl-2">{editableReport.testDate}</span>
            )}
          </div>
        </div>

        {/* Subject & Client Info */}
        <div className="mb-6 grid grid-cols-2">
          <div className="rounded border border-black p-2">
            <span className="font-bold">SUBJECT:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.subject}</span>
            )}
          </div>
          <div className="rounded border border-black p-2">
            <span className="font-bold">Client - M/s :</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.client.name}
                onChange={(e) => handleNestedInputChange('client', 'name', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.client.name}</span>
            )}
          </div>
        </div>

        {/* Report Details Grid */}
        <div className="grid grid-cols-2 gap-x-2 border-l border-t border-black">
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Job Description:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.jobDescription}
                onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.jobDescription}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Equipment Specification:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.equipment.specification}
                onChange={(e) => handleNestedInputChange('equipment', 'specification', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.equipment.specification}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Equipment:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.equipment.name}
                onChange={(e) => handleNestedInputChange('equipment', 'name', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.equipment.name}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Material Grade:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.materialGrade}
                onChange={(e) => handleInputChange('materialGrade', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.materialGrade}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Serial No:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.equipment.serialNumber}
                onChange={(e) => handleNestedInputChange('equipment', 'serialNumber', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.equipment.serialNumber}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Heat No.:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.heatNumber}
                onChange={(e) => handleInputChange('heatNumber', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.heatNumber}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Date of Calibration:</span>
            {isEditing ? (
              <input
                type="date"
                value={editableReport.equipment.calibrationDate}
                onChange={(e) => handleNestedInputChange('equipment', 'calibrationDate', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.equipment.calibrationDate}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Surface Condition:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.surfaceCondition}
                onChange={(e) => handleInputChange('surfaceCondition', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.surfaceCondition}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Method:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.method}
                onChange={(e) => handleInputChange('method', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.method}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Basic Calibration Block:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.basicCalibrationBlock}
                onChange={(e) => handleInputChange('basicCalibrationBlock', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.basicCalibrationBlock}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Couplant:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.couplant}
                onChange={(e) => handleInputChange('couplant', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.couplant}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Reference Calibration Block:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.referenceCalibrationBlock}
                onChange={(e) => handleInputChange('referenceCalibrationBlock', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.referenceCalibrationBlock}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Reference Gain Level:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.referenceGainLevel}
                onChange={(e) => handleInputChange('referenceGainLevel', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.referenceGainLevel}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Evaluation DB:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.evaluationDb}
                onChange={(e) => handleInputChange('evaluationDb', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.evaluationDb}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Scanning DB:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.scanningDb}
                onChange={(e) => handleInputChange('scanningDb', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.scanningDb}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Test Range:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testRange}
                onChange={(e) => handleInputChange('testRange', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.testRange}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Reference Standard:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.referenceStandard}
                onChange={(e) => handleInputChange('referenceStandard', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.referenceStandard}</span>
            )}
          </div>
          <div className="border-b border-r border-black p-2">
            <span className="font-bold">Acceptance Criteria:</span>
            {isEditing ? (
              <input
                type="text"
                value={editableReport.acceptanceCriteria}
                onChange={(e) => handleInputChange('acceptanceCriteria', e.target.value)}
                className="ml-2 bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 w-full"
              />
            ) : (
              <span> {editableReport.acceptanceCriteria}</span>
            )}
          </div>
        </div>

        {/* Table Header */}
        <div className="mt-6 flex border-l border-t border-black">
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Amount of coverage/Scan
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Frequency MHz
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Type of Probe
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Size
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Angle
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            Scanning Overlap
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center font-bold">
            (dB) SCAN
          </div>
        </div>

        {/* Table Data Row 1 */}
        <div className="flex border-l border-black">
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.coverage}
                onChange={(e) => handleNestedInputChange('testDetails', 'coverage', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.coverage
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.frequency}
                onChange={(e) => handleNestedInputChange('testDetails', 'frequency', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.frequency
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.probeType}
                onChange={(e) => handleNestedInputChange('testDetails', 'probeType', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.probeType
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.size}
                onChange={(e) => handleNestedInputChange('testDetails', 'size', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.size
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.angle}
                onChange={(e) => handleNestedInputChange('testDetails', 'angle', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.angle
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.scanningOverlap}
                onChange={(e) => handleNestedInputChange('testDetails', 'scanningOverlap', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.scanningOverlap
            )}
          </div>
          <div className="flex-1 border-b border-r border-black p-2 text-center">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.testDetails.scanDb}
                onChange={(e) => handleNestedInputChange('testDetails', 'scanDb', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
              />
            ) : (
              editableReport.testDetails.scanDb
            )}
          </div>
        </div>

        {/* Table Header 2 */}
        <div className="mt-6 flex border-l border-t border-black">
          <div className="w-1/4 border-b border-r border-black p-2 font-bold">
            Details:
          </div>
          <div className="flex-grow border-b border-r border-black p-2">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.jobDescription}
                onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
              />
            ) : (
              editableReport.jobDescription
            )}
          </div>
        </div>

        {/* Table 3 Headers */}
        <div className="flex border-l border-t border-black">
          <div className="w-[5%] border-b border-r border-black p-2 text-center font-bold">
            Sr. No.
          </div>
          <div className="w-[15%] border-b border-r border-black p-2 text-center font-bold">
            Job Description
          </div>
          <div className="w-[15%] border-b border-r border-black p-2 text-center font-bold">
            Heat No.
          </div>
          <div className="w-[15%] border-b border-r border-black p-2 text-center font-bold">
            Quantity/KG
          </div>
          <div className="w-[30%] border-b border-r border-black p-2 text-center font-bold">
            Observation
          </div>
          <div className="w-[20%] border-b border-r border-black p-2 text-center font-bold">
            Remarks
          </div>
        </div>

        {/* Table 3 Data Rows */}
        {editableReport.items.map((item, index) => (
          <div key={index} className="flex border-l border-black">
            <div className="w-[5%] border-b border-r border-black p-2 text-center">
              {item.srNo}
            </div>
            <div className="w-[15%] border-b border-r border-black p-2">
              {isEditing ? (
                <input
                  type="text"
                  value={item.jobDescription}
                  onChange={(e) => handleItemChange(index, 'jobDescription', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                />
              ) : (
                item.jobDescription
              )}
            </div>
            <div className="w-[15%] border-b border-r border-black p-2">
              {isEditing ? (
                <input
                  type="text"
                  value={item.heatNumber}
                  onChange={(e) => handleItemChange(index, 'heatNumber', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                />
              ) : (
                item.heatNumber
              )}
            </div>
            <div className="w-[15%] border-b border-r border-black p-2 text-center">
              {isEditing ? (
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                />
              ) : (
                item.quantity
              )}
            </div>
            <div className="w-[30%] border-b border-r border-black p-2">
              {isEditing ? (
                <input
                  type="text"
                  value={item.observation}
                  onChange={(e) => handleItemChange(index, 'observation', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                />
              ) : (
                item.observation
              )}
            </div>
            <div className="w-[20%] border-b border-r border-black p-2">
              {isEditing ? (
                <input
                  type="text"
                  value={item.remarks}
                  onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                  className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                />
              ) : (
                item.remarks
              )}
            </div>
          </div>
        ))}

        {/* Add/Remove Items for Editing */}
        {isEditing && (
          <div className="flex border-l border-black">
            <div className="w-[5%] border-b border-r border-black p-2 text-center">
              <button
                onClick={addItem}
                className="text-blue-500 hover:text-blue-700 font-bold"
                title="Add Item"
              >
                +
              </button>
            </div>
            <div className="w-[15%] border-b border-r border-black p-2 text-center text-gray-500">
              Add Item
            </div>
            <div className="w-[15%] border-b border-r border-black p-2 text-center text-gray-500">
              -
            </div>
            <div className="w-[15%] border-b border-r border-black p-2 text-center text-gray-500">
              -
            </div>
            <div className="w-[30%] border-b border-r border-black p-2 text-center text-gray-500">
              -
            </div>
            <div className="w-[20%] border-b border-r border-black p-2 text-center text-gray-500">
              -
            </div>
          </div>
        )}

        {/* Add empty rows for additional entries */}
        {Array.from({ length: Math.max(0, 4 - editableReport.items.length) }).map((_, index) => (
          <div key={`empty-${index}`} className="flex border-l border-black">
            <div className="w-[5%] border-b border-r border-black p-2 h-8"></div>
            <div className="w-[15%] border-b border-r border-black p-2 h-8"></div>
            <div className="w-[15%] border-b border-r border-black p-2 h-8"></div>
            <div className="w-[15%] border-b border-r border-black p-2 h-8"></div>
            <div className="w-[30%] border-b border-r border-black p-2 h-8"></div>
            <div className="w-[20%] border-b border-r border-black p-2 h-8"></div>
          </div>
        ))}

        <div className="flex border-l border-black">
          <div className="w-[5%] border-b border-r border-black p-2 font-bold">Remark:</div>
          <div className="w-[95%] border-b border-r border-black p-2">
            {isEditing ? (
              <input
                type="text"
                value={editableReport.remark}
                onChange={(e) => handleInputChange('remark', e.target.value)}
                className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
              />
            ) : (
              editableReport.remark
            )}
          </div>
        </div>

        {/* Signature Section */}
        {/* <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="font-bold">Name of the Inspector</p>
            <p className="border-t border-dashed border-gray-400 pt-6">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.name}
                  onChange={(e) => handleNestedInputChange('inspector', 'name', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                />
              ) : (
                editableReport.inspector.name
              )}
            </p>
            <p>
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.certification}
                  onChange={(e) => handleNestedInputChange('inspector', 'certification', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                />
              ) : (
                editableReport.inspector.certification
              )}
            </p>
            <p>
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.level}
                  onChange={(e) => handleNestedInputChange('inspector', 'level', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                />
              ) : (
                `(${editableReport.inspector.level})`
              )}
            </p>
          </div>
          <div className="text-center">
            <p className="font-bold">Test Carried Out in presence of</p>
            <p className="border-t border-dashed border-gray-400 pt-6">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.witness}
                  onChange={(e) => handleInputChange('witness', e.target.value)}
                  className="bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400 text-center"
                />
              ) : (
                editableReport.witness
              )}
            </p>
          </div>
        </div> */}
                {/* ✅ UPDATED: Three-Column Signature Section with More Space */}
              {/* ✅ FINAL: Three-Column Signature Section — Content Snug at Bottom */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border border-black p-4">
          {/* Column 1: Name of the Performer */}
          <div className="text-center flex flex-col h-[120px]">
            <p className="font-bold text-xs mb-1">Name of the Performer</p>
            <div className="border-t border-dashed border-gray-400 pt-2 pb-1 min-h-[20px]">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.name}
                  onChange={(e) => handleNestedInputChange('inspector', 'name', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500 mt-10"
                  placeholder="Inspector Name"
                />
              ) : (
                editableReport.inspector.name || '—'
              )}
            </div>
            {/* Push content to bottom */}
            <div className="mt-auto text-xs leading-tight">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.certification}
                  onChange={(e) => handleNestedInputChange('inspector', 'certification', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Certification"
                />
              ) : (
                editableReport.inspector.certification || '—'
              )}
            </div>
            <div className="text-xs mt-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.level}
                  onChange={(e) => handleNestedInputChange('inspector', 'level', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Level"
                />
              ) : (
                `(${editableReport.inspector.level || '—'})`
              )}
            </div>
            <div className="font-bold text-xs mt-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.inspector.company || "AVI'S ENGINEERING & CONSULTANCY"}
                  onChange={(e) => handleNestedInputChange('inspector', 'company', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Company"
                />
              ) : (
                editableReport.inspector.company || "AVI'S ENGINEERING & CONSULTANCY"
              )}
            </div>
          </div>

          {/* Column 2: Test Carried Out in presence of */}
          <div className="text-center flex flex-col h-[120px]">
            <p className="font-bold text-xs mb-1">Test Carried Out in presence of</p>
            <div className="border-t border-dashed border-gray-400 pt-2 pb-1 min-h-[20px]">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.witness}
                  onChange={(e) => handleInputChange('witness', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500 mt-10"
                  placeholder="Witness Name"
                />
              ) : (
                editableReport.witness || '—'
              )}
            </div>
            {/* Push content to bottom */}
            <div className="mt-auto text-xs">
              Client Representative
            </div>
            <div className="font-bold text-xs mt-1">
              {editableReport.client.name}
            </div>
          </div>

          {/* Column 3: Witnessed by */}
          <div className="text-center flex flex-col h-[120px]">
            <p className="font-bold text-xs mb-1">Witnessed by</p>
            <div className="border-t border-dashed border-gray-400 pt-2 pb-1 min-h-[20px]">
              {isEditing ? (
                <input
                  type="text"
                  value={editableReport.witnessedBy || ''}
                  onChange={(e) => handleInputChange('witnessedBy', e.target.value)}
                  className="w-full bg-transparent text-center focus:outline-none focus:ring-1 focus:ring-blue-500 mt-10"
                  placeholder="Authorized Signatory"
                />
              ) : (
                editableReport.witnessedBy || '__________________'
              )}
            </div>
            {/* Push content to bottom */}
            <div className="mt-auto text-xs">
              Authorized Signatory
            </div>
            <div className="font-bold text-xs mt-1">
              {editableReport.client.name}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-6 text-xs text-gray-700">
          <p className="font-bold">Notes:</p>
          <ul className="list-inside list-disc">
            {editableReport.notes.map((note, index) => (
              <li key={index}>
                {isEditing ? (
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => {
                      const updatedNotes = [...editableReport.notes];
                      updatedNotes[index] = e.target.value;
                      handleInputChange('notes', updatedNotes);
                    }}
                    className="w-full bg-transparent focus:outline-none focus:border-blue-500 border-b border-dotted border-gray-400"
                  />
                ) : (
                  note
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EditableUTReport;